import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

// Normalize returned chatbot base URL so iframe uses secure scheme on HTTPS pages
const normalizeChatbotBaseUrl = (base) => {
  if (!base) return base;
  try {
    const u = new URL(base, window.location.origin);
    if (u.protocol === "http:" && window.location.protocol === "https:") {
      u.protocol = "https:";
    }
    return u.toString();
  } catch (err) {
    // If value is a bare host like "example.com", ensure it uses current page protocol
    if (/^[^/:]+\.[^/:]+/.test(base) && !base.startsWith("http") && !base.startsWith("/")) {
      return `${window.location.protocol}//${base}`;
    }
    return base;
  }
};

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const [chatbotBaseUrl,   setChatbotBaseUrl]   = useState(null);
  const [chatbotAssistant, setChatbotAssistant] = useState("hiresmart360");
  const [chatbotEnabled,   setChatbotEnabled]   = useState(false); // off until loads
  const [configLoading,    setConfigLoading]    = useState(true);
  const [configError,      setConfigError]      = useState(null);
 
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch("/api/configChatbot/frontend");
        if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
        const data = await res.json();

        // Ensure the returned base URL won't cause mixed-content on HTTPS pages
        setChatbotBaseUrl(normalizeChatbotBaseUrl(data.chatbotBaseUrl ?? null));
        setChatbotAssistant(data.chatbotAssistant ?? "hiresmart360");
        setChatbotEnabled(data.chatbotEnabled ?? false);
      } catch (err) {
        console.warn("[ChatContext] Could not load chatbot config, chatbot disabled:", err.message);
        setChatbotEnabled(false);
        setConfigError(err.message);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const [chatOpen,      setChatOpen]      = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [chatExpanded,  setChatExpanded]  = useState(false);

  const openChat      = useCallback(() => { setChatOpen(true); setChatMinimized(false); }, []);
  const closeChat      = useCallback(() => { 
    setChatOpen(false); 
    setChatMinimized(false); 
    setChatExpanded(false); 
  }, []);  
  const toggleMinimize = useCallback(() => setChatMinimized(m => !m), []);
  const toggleExpand  = useCallback(() => setChatExpanded(e => !e), []);

  const value = {
    chatbotBaseUrl, 
    chatbotAssistant, 
    chatbotEnabled, 
    configLoading, 
    configError,

    chatOpen, 
    chatMinimized, 
    chatExpanded,
    openChat, 
    closeChat, 
    toggleMinimize, 
    toggleExpand,
  }
  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);